/* eslint-disable */
import React, { useEffect, useState } from "react";
import axios, { AxiosError } from "axios";
import { useAppSelector } from "@redux/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import {
  getUserId,
  getUserIdglobal,
  getroles,
  getrolesglobal,
} from "./request/request";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";

const API_URL = process.env.REACT_APP_API_URL || "";

type Rol = {
  name: string;
  id: number;
  description?: string;
};

type User = {
  firstName: string;
  lastName: string;
  email: string;
  NumberPhone: string;
  position: string;
  Roles: Rol[];
  Tenant: string;
  password?: string;
};

interface ViewModalUpdateProps {
  setID: (id: string | null | undefined) => void;
  id?: string | null;
  fetchUsers: () => Promise<void>;
}

export function ViewModalUpdate({
  setID,
  id,
  fetchUsers,
}: ViewModalUpdateProps) {
  // const tenant = useAppSelector((state) => state.tenant.id);
  const { tenantId } = useAppSelector((state) => state.selection);
  const { getAccessTokenSilently } = useAuth0();

  // userData => estado que el usuario está editando en el formulario
  const [userData, setUserData] = useState<User>({
    firstName: "",
    lastName: "",
    email: "",
    NumberPhone: "",
    position: "",
    Roles: [],
    Tenant: "",
  });

  // originalData => para comparar y detectar cambios en modo "Edit"
  const [originalData, setOriginalData] = useState<User | null>(null);

  const [roles, setRoles] = useState<Rol[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  // Optional: reset feedback when opening/closing or switching id
  useEffect(() => {
    setFeedback(null);
  }, [id]);

  // Overlay para spinner
  const overlaySpinner = useProcessingOverlay(isFetching);

  // Saber si es modo "Add" o "Edit"
  const isEditMode = Boolean(id);

  // Al montar o cambiar "id", cargamos datos si estamos en modo Edit
  useEffect(() => {
    const fetchData = async () => {
      if (id === undefined) return; // significa que no abrimos el modal

      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();

        // Modo "Edit": cargar usuario
        if (id) {
          const userResponse =
            tenantId === 0
              ? await getUserId(token, id)
              : await getUserIdglobal(token, id, tenantId);
          setUserData(userResponse.data);
          setOriginalData(userResponse.data); // guardamos la data original
        }

        // Cargar roles
        const rolesResponse =
          tenantId === 0
            ? await getroles(token)
            : await getrolesglobal(token, tenantId);
        setRoles(rolesResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [id, tenantId, getAccessTokenSilently]);

  // Función que compara userData con originalData para saber si hubo cambios
  function hasChanges() {
    if (!originalData) return false;
    // Only compare fields we actually edit
    const keys: (keyof User)[] = [
      "firstName",
      "lastName",
      "email",
      "NumberPhone",
      "position",
    ];
    const scalarChanged = keys.some(
      (k) => (userData[k] ?? "") !== (originalData[k] ?? "")
    );
    const roleChanged =
      (userData.Roles[0]?.id ?? null) !== (originalData.Roles[0]?.id ?? null);
    return scalarChanged || roleChanged;
  }
  // Validar si los campos requeridos están llenos y, en modo Edit, si hay cambios
  function isFormValid() {
    // Requeridos: firstName, email, roles
    const hasRequiredFields =
      userData.firstName.trim() !== "" &&
      userData.email.trim() !== "" &&
      userData.Roles.length > 0;

    if (!hasRequiredFields) {
      return false;
    }

    // Modo "Add": con que los campos requeridos no estén vacíos, basta
    if (!isEditMode) {
      return true;
    }

    // Modo "Edit": además de requeridos, debe haber algún cambio
    return hasChanges();
  }

  const AUTO_CLOSE_ON_SUCCESS = true; //to false if we want it open
  const AUTO_CLOSE_MS = 1500;

  const tenantQ = (t: number | null | undefined) =>
    t === null || t === undefined ? "" : `?tenantId=${t}`;
  type Feedback = { type: "success" | "error"; text: string } | null;


  const handleSave = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const token = await getAccessTokenSilently();
      const endpoint = isEditMode
        ? `${API_URL}/v1/auth0/users/update/${id}${tenantQ(tenantId)}`
        : `${API_URL}/v1/auth0/users/create/${tenantQ(tenantId)}`;

      const req = isEditMode
        ? axios.put(endpoint, userData, {
            headers: { Authorization: `Bearer ${token}` },
          })
        : axios.post(endpoint, userData, {
            headers: { Authorization: `Bearer ${token}` },
          });

      const resp = await req; // expect updated user back

      // Normalize response -> minimal subset we care about
      const updated: User = {
        firstName: resp.data.firstName ?? userData.firstName,
        lastName: resp.data.lastName ?? userData.lastName,
        email: resp.data.email ?? userData.email,
        NumberPhone: resp.data.NumberPhone ?? userData.NumberPhone,
        position: resp.data.position ?? userData.position,
        Roles: Array.isArray(resp.data.Roles)
          ? resp.data.Roles
          : userData.Roles,
        Tenant: userData.Tenant, // ignore object from backend to avoid type mismatch here
      };

      // 1) Update modal fields immediately so the user sees changes
      setUserData(updated);
      setOriginalData(updated);

      // 2) Success banner
      setFeedback({
        type: "success",
        text: isEditMode
          ? "User updated successfully."
          : "User created successfully.",
      });

      // 3) Refresh listing (UsersPage will pick up dirty flag via host) OR call direct
      await fetchUsers();

      // 4) Optional auto-close
      if (AUTO_CLOSE_ON_SUCCESS) {
        setTimeout(() => setID(undefined), AUTO_CLOSE_MS);
      }
    } catch (e) {
        const parsed = parseAxiosError(e);
        setFeedback({ type: "error", text: parsed.message });
      console.error("Error saving user =>", e);
    } finally {
      setLoading(false);
    }
  };

  // const handleSave = async () => {
  //     setLoading(true);
  //     setFeedback(null);
  //     try {
  //         const token = await getAccessTokenSilently();
  //         const endpoint = isEditMode
  //             ? `${API_URL}/v1/auth0/users/update/${id}${tenantId ? `?tenantId=${tenantId}` : ""
  //             }`
  //             : `${API_URL}/v1/auth0/users/create/${tenantId ? `?tenantId=${tenantId}` : ""
  //             }`;

  //         if (isEditMode) {
  //             await axios.put(endpoint, userData, {
  //                 headers: { Authorization: `Bearer ${token}` },
  //             });
  //         } else {
  //             await axios.post(endpoint, userData, {
  //                 headers: { Authorization: `Bearer ${token}` },
  //             });
  //         }
  //         setFeedback({ type: "success", text: isEditMode ? "User updated successfully." : "User created successfully." });
  //         setID(undefined);
  //         fetchUsers();
  //     } catch (error: unknown) {
  //         const err = error as AxiosError;
  //         console.error("Error saving user =>", err);
  //         setFeedback({ type: "error", text: parseAxiosError(err) });
  //         // Manejo de error con tu alerta o como gustes
  //     } finally {
  //         setLoading(false);
  //     }
  // };

  return (
    <>
      <GenericModal
        isOpen={id !== undefined}
        onClose={() => setID(undefined)}
        title={id ? "Edit User" : "Add User"}
        size="lg"
      >
        {feedback && (
          <div
            className={`mb-4 px-3 py-2 rounded text-sm ${
              feedback.type === "success"
                ? "bg-green-100 text-green-800 border border-green-200"
                : "bg-red-100 text-red-800 border border-red-200"
            }`}
          >
            {feedback.text}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* First Name (requerido) */}
          <GenericInput
            label={
              <span>
                First Name <span className="text-red-500">*</span>
              </span>
            }
            value={userData.firstName}
            onChange={(v) => setUserData({ ...userData, firstName: v })}
          />

          {/* Last Name (opcional) */}
          <GenericInput
            label="Last Name"
            value={userData.lastName}
            onChange={(v) => setUserData({ ...userData, lastName: v })}
          />

          {/* Email (requerido) */}
          <GenericInput
            label={
              <span>
                Email <span className="text-red-500">*</span>
              </span>
            }
            value={userData.email}
            disabled={isEditMode} // en tu caso, si no quieres permitir cambiar email al editar
            onChange={(v) => setUserData({ ...userData, email: v })}
          />

          {/* Phone Number (opcional) */}
          <GenericInput
            label="Phone Number"
            value={userData.NumberPhone}
            onChange={(v) => setUserData({ ...userData, NumberPhone: v })}
          />

          {/* Position (opcional) */}
          <GenericInput
            label="Position"
            value={userData.position}
            onChange={(v) => setUserData({ ...userData, position: v })}
          />

          {/* Roles (requerido) */}
          <div className="col-span-1">
            <label className="text-sm font-medium">
              Roles <span className="text-red-500">*</span>
            </label>
            <GenericDropdown
              items={roles.map((r) => r.name)}
              value={userData.Roles[0]?.name || ""}
              onSelect={(name) => {
                const role = roles.find((r) => r.name === name);
                if (role) {
                  setUserData({
                    ...userData,
                    Roles: [role],
                  });
                }
              }}
              defaultLabel="Select Role"
              widthClass="w-full"
              maxWidthClass="max-w-[400px]"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <GenericButton
            variant="secondary"
            label="Cancel"
            onClick={() => setID(undefined)}
          />
          <GenericButton
            variant="brand"
            label={loading ? "Saving..." : "Save"}
            onClick={handleSave}
            // Deshabilitado si no es válido el form o si está en loading
            disabled={loading || !isFormValid()}
          />
        </div>
      </GenericModal>

      {overlaySpinner}
    </>
  );
}

export default ViewModalUpdate;
